/**
 * Helper unico de envio de mensagens WhatsApp.
 *
 * Substitui chamadas diretas a `supabase.functions.invoke('whatsapp-360dialog-send', ...)`.
 * Detecta o provider da conexao via conversation_id ou connection_id (no body) e
 * roteia internamente para a edge function correta:
 *   - provider 'evolution' -> whatsapp-evolution-send
 *   - default (360dialog)  -> whatsapp-360dialog-send
 *
 * Mantem o mesmo shape de body e o mesmo formato de retorno. O componente
 * que chama nao precisa saber qual provider esta por tras.
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

type SendBody = {
  to?: string;
  telefone?: string;
  text?: string;
  conversation_id?: string;
  connection_id?: string;
  message_id?: string;
  media_url?: string;
  media_type?: 'image' | 'audio' | 'video' | 'document';
  mime_type?: string;
  filename?: string;
  caption?: string;
  // Campos específicos de 360Dialog (templates HSM) — só são usados se o
  // provider for 360dialog. Em Evolution eles são ignorados.
  template_name?: string;
  variaveis?: Array<{ pos: number; valor: string }>;
  campanha_id?: string;
  destinatario_id?: string;
  voice?: boolean;
  header_media_url?: string;
  header_filename?: string;
  header_media_type?: 'document' | 'image' | 'video';
};

type SendResult = {
  data: any;
  error: { message: string } | null;
};

async function resolveProvider(
  client: SupabaseClient,
  body: SendBody,
): Promise<'evolution' | '360dialog' | null> {
  // 1. connection_id explícito
  if (body.connection_id) {
    const { data } = await client
      .from('whatsapp_connections')
      .select('provider')
      .eq('id', body.connection_id)
      .maybeSingle();
    return (data?.provider as 'evolution' | '360dialog') ?? null;
  }
  // 2. via conversation_id
  if (body.conversation_id) {
    const { data: conv } = await client
      .from('whatsapp_conversations')
      .select('connection_id')
      .eq('id', body.conversation_id)
      .maybeSingle();
    if (conv?.connection_id) {
      const { data } = await client
        .from('whatsapp_connections')
        .select('provider')
        .eq('id', conv.connection_id)
        .maybeSingle();
      return (data?.provider as 'evolution' | '360dialog') ?? null;
    }
  }
  return null;
}

export async function sendWhatsAppMessage(body: SendBody): Promise<SendResult> {
  // EVOLUTION-ONLY: 360dialog NÃO existe mais — não há mais caminho pro 360 (ele montava
  // "use_secret/v1/messages" e quebrava). Todo envio vai pelo Evolution.
  const fnName = 'whatsapp-evolution-send';

  // RETRY SEGURO (sem duplicar): só re-tenta quando o BACKEND está inalcançável
  // (deploy/restart do servidor, rede) — nesse caso a mensagem NÃO foi processada,
  // então reenviar não duplica. Falha de nível WhatsApp (success:false, ex.: Evolution
  // 500/timeout) NÃO é re-tentada aqui — o backend já recebeu e poderia duplicar pro
  // cliente; quem chamou trata (mostra falha / reenviar manual).
  const delays = [2000, 5000, 12000, 20000];
  let result: SendResult = { data: null, error: { message: 'sem tentativa' } };
  for (let attempt = 0; attempt <= delays.length; attempt++) {
    try {
      result = await supabase.functions.invoke(fnName, { body });
    } catch (e: any) {
      result = { data: null, error: { message: e?.message || 'Falha de rede no envio' } };
    }
    // Sem erro de invoke = chegou no backend (sucesso OU success:false): retorna.
    if (!result.error) return result;
    const msg = String(result.error.message || '').toLowerCase();
    const backendIndisponivel =
      msg.includes('fetch') || msg.includes('network') || msg.includes('failed to send') ||
      msg.includes('502') || msg.includes('503') || msg.includes('504') ||
      msg.includes('gateway') || msg.includes('econnrefused') || msg.includes('timeout');
    if (!backendIndisponivel || attempt === delays.length) return result;
    // Servidor subindo/indisponível: espera e tenta de novo (a msg segue 'pending' na tela).
    await new Promise((r) => setTimeout(r, delays[attempt]));
  }
  return result;
}
